import { useState } from 'react'
import { DDCAScope } from '@models/index'
import './Gate1Renderer.css'

interface Gate1RendererProps {
  data: DDCAScope & { user_query?: string }
  onApprove: () => void
  onModify: (modifications: Partial<DDCAScope>) => void
}

export function Gate1Renderer({ data, onApprove }: Gate1RendererProps) {
  const [approved, setApproved] = useState(false)
  // Extract user query and intent
  const userQuery = (data as any).user_query || data.constants.primary_axiom || ''
  const queryIntent = data.constants.query_intent || 'STRATEGIC'

  // Extract terms from framework
  const terms = Array.isArray(data.framework.terms) ? data.framework.terms : []

  // Extract key concepts from terms for visual map
  const keyTerms = terms.slice(0, 4).map((term: any) => {
    if (typeof term === 'object' && term !== null) {
      return term.term || term.name || term.label || 'Concept'
    }
    return String(term)
  })

  // Extract analysis categories dynamically from DDCA deductive scope
  const extractAnalysisCategories = (): string[] => {
    // Option 1: Use semantic_domains from deductive_scope
    const deductiveScope = data.framework?.deductive_scope || {}
    if (Array.isArray(deductiveScope.semantic_domains) && deductiveScope.semantic_domains.length > 0) {
      return deductiveScope.semantic_domains
    }

    // Option 2: Extract unique domains from terms
    if (Array.isArray(terms) && terms.length > 0) {
      const uniqueDomains = Array.from(
        new Set(
          terms
            .filter((term: any) => term && typeof term === 'object' && term.domain)
            .map((term: any) => term.domain)
        )
      )
      if (uniqueDomains.length > 0) {
        return uniqueDomains
      }
    }

    // Option 3: Fallback based on query intent
    if (queryIntent === 'DIAGNOSTIC') {
      return ['Root Cause Analysis', 'Process & Capability Assessment']
    } else if (queryIntent === 'FACTUAL') {
      return ['Data & Metrics Discovery', 'Documentation Review']
    }

    // Default fallback for STRATEGIC or unknown
    return ['Strategic Opportunities', 'Competitive Positioning']
  }

  const analysisCategories = extractAnalysisCategories()

  return (
    <div className="gate1-container">
      {/* User Question */}
      <section className="question-section">
        <h4>Your Question</h4>
        <div className="question-text">
          "{userQuery}"
        </div>
      </section>

      {/* Visual Concept Map */}
      <section className="concept-map-section">
        <h4>What We'll Focus Upon</h4>
        {keyTerms.length > 0 && (
          <div className="concept-terms">
            {keyTerms.map((term: string, idx: number) => (
              <span key={idx} className="concept-term-item">
                {term}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* What We'll Analyze - Dynamic based on DDCA semantic domains */}
      <section className="analysis-section">
        <h4>What We'll Analyze</h4>
        <div className="analysis-list">
          {analysisCategories.map((category, idx) => (
            <div key={idx} className="analysis-item">
              <span className="check-icon">✓</span>
              <span>{category}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="gate-actions">
        <button className="modify-btn" onClick={() => alert('Modify functionality: adjust focus areas or refine question')}>
          Modify
        </button>
        <button
          className={`approve-btn primary ${approved ? 'approved' : ''}`}
          onClick={() => {
            setApproved(true)
            onApprove()
          }}
        >
          Approve & Continue to Gate 2 →
        </button>
      </div>
    </div>
  )
}
